/**
 * Stage 73.20u — Legacy Payroll Variances drill-down.
 *
 * Opens from the Imports list once an import has variances computed.
 * Shows the per-head variance summary at top, the full list of variance
 * rows (employee × head) below with action picker + approval flow,
 * and CSV exports.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Loader2, ArrowLeft, Download, Filter, AlertTriangle,
  CheckCircle2, ChevronRight, X, Lock, FileWarning,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const ACTION_LABEL = {
  adjust_in_next_payroll: 'Auto-adjust in next payroll',
  recover_from_employee: 'Recover from employee',
  refund_to_employee: 'Refund to employee',
  employer_adjustment_only: 'Employer absorbs (no employee impact)',
  ignore_with_approval: 'Ignore with approval',
  statutory_correction_required: 'Statutory correction (escalate)',
};

const STATUS_TONE = {
  open: 'bg-rose-50 text-rose-700 border-rose-300',
  actioned: 'bg-amber-50 text-amber-700 border-amber-300',
  approved: 'bg-blue-50 text-blue-700 border-blue-300',
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
};

const HEAD_LABEL = {
  employee_pf: 'Employee PF',
  employee_esi: 'Employee ESI',
  pt: 'Professional Tax',
  tds: 'TDS',
  employer_pf: 'Employer PF',
  employer_esi: 'Employer ESI',
  net_pay: 'Net Pay',
};

export default function LegacyPayrollVariances() {
  const { import_id } = useParams();
  const [imp, setImp] = useState(null);
  const [variances, setVariances] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterHead, setFilterHead] = useState('all');
  const [actionTarget, setActionTarget] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/legacy-payroll/imports/${import_id}`, { withCredentials: true });
      const d = r.data || {};
      setImp(d.import || null);
      // Stage 73.20ce — Always normalise to arrays so downstream
      // `.length`, `.map`, `.filter` calls can never crash.
      setVariances(Array.isArray(d.variances) ? d.variances : []);
      setLines(Array.isArray(d.lines) ? d.lines : []);
    } catch (e) {
      setImp(null); setVariances([]); setLines([]);
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [import_id]);

  useEffect(() => { loadAll(); }, [import_id, loadAll]);


  const filtered = useMemo(() => {
    const list = Array.isArray(variances) ? variances : [];
    return list.filter(v =>
      (filterStatus === 'all' || v.status === filterStatus) &&
      (filterHead === 'all' || v.head === filterHead),
    );
  }, [variances, filterStatus, filterHead]);

  const summary = useMemo(() => {
    const out = { open: 0, actioned: 0, approved: 0, closed: 0, total_diff: 0 };
    const list = Array.isArray(variances) ? variances : [];
    list.forEach(v => {
      out[v.status] = (out[v.status] || 0) + 1;
      out.total_diff += Math.abs(Number(v.difference || 0));
    });
    return out;
  }, [variances]);

  const downloadCsv = async (kind) => {
    const url = kind === 'pending'
      ? `${API}/legacy-payroll/reports/pending-adjustments.csv?company_id=${imp?.company_id || ''}`
      : `${API}/legacy-payroll/reports/variances.csv?import_id=${import_id}`;
    try {
      const r = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const dl = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = dl;
      a.download = kind === 'pending' ? 'pending_adjustments.csv' : `legacy_variances_${import_id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(dl);
    } catch (e) { toast.error('Download failed'); }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
        </div>
      </MainLayout>
    );
  }

  if (!imp) {
    return (
      <MainLayout>
        <Card className="p-6 text-center">
          <p className="text-slate-600">Import not found.</p>
          <Link to="/payroll/legacy-imports" className="text-sm text-amber-700 underline mt-2 inline-block">
            ← Back to Imports
          </Link>
        </Card>
      </MainLayout>
    );
  }

  const period = `${MONTHS[(imp.statutory_month || 1) - 1]} ${imp.statutory_year}`;
  const isFinalized = imp.status === 'finalized';

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="legacy-variances-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/payroll/legacy-imports" className="text-sm text-amber-700 hover:underline inline-flex items-center gap-1 mb-2">
              <ArrowLeft className="w-3.5 h-3.5" /> All Legacy Imports
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">
              Variances — {imp.name}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Period: <strong>{period}</strong> • Status:{' '}
              <span className="text-amber-700 font-semibold">{imp.status?.replace(/_/g, ' ')}</span>
              {isFinalized && <Lock className="w-3.5 h-3.5 inline ml-2 text-emerald-600" />}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => downloadCsv('variances')} data-testid="export-variances-csv">
              <Download className="w-4 h-4 mr-1" /> Export Variances CSV
            </Button>
            <Button variant="outline" onClick={() => downloadCsv('pending')} data-testid="export-pending-csv">
              <Download className="w-4 h-4 mr-1" /> Pending Adjustments CSV
            </Button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SumTile label="Total Variances" value={variances.length} tone="slate" />
          <SumTile label="Open" value={summary.open} tone="rose" />
          <SumTile label="Actioned" value={summary.actioned} tone="amber" />
          <SumTile label="Approved" value={summary.approved} tone="blue" />
          <SumTile label="Closed" value={summary.closed} tone="emerald" />
        </div>

        {/* Per-head pivot */}
        {imp.variance_summary && (
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <FileWarning className="w-4 h-4 text-amber-600" /> Per-head variance summary
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-600 bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Head</th>
                    <th className="text-right px-3 py-2">Rows with diff</th>
                    <th className="text-right px-3 py-2">Net Diff (Actual − System)</th>
                    <th className="text-left px-3 py-2 pl-6">Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(imp.variance_summary || {}).map(([head, s]) => {
                    if (!s?.rows_with_diff) return null;
                    const diff = Number(s.diff_sum || 0);
                    return (
                      <tr key={head} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium">{HEAD_LABEL[head] || head}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{s.rows_with_diff}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${diff > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {diff > 0 ? '+' : ''}₹{Math.round(diff).toLocaleString('en-IN')}
                        </td>
                        <td className="px-3 py-2 pl-6 text-slate-600">
                          {diff > 0
                            ? <span className="text-rose-700">Over-deducted / over-paid actual → recover or refund</span>
                            : <span className="text-emerald-700">Under-deducted / under-paid → may need top-up</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Filter chips */}
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-slate-600">Status:</span>
            {['all', 'open', 'actioned', 'approved', 'closed'].map(s => (
              <button
                key={s}
                data-testid={`variance-filter-status-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 rounded-full border font-medium capitalize ${filterStatus === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                {s}
              </button>
            ))}
            <span className="text-slate-600 ml-3">Head:</span>
            <select
              data-testid="variance-filter-head"
              value={filterHead}
              onChange={e => setFilterHead(e.target.value)}
              className="px-3 py-1 border rounded text-xs">
              <option value="all">All heads</option>
              {Object.entries(HEAD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </Card>

        {/* Variances table */}
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-800">Variances ({filtered.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Employee</th>
                  <th className="text-left px-4 py-2">Head</th>
                  <th className="text-right px-4 py-2">Actual</th>
                  <th className="text-right px-4 py-2">System</th>
                  <th className="text-right px-4 py-2">Difference</th>
                  <th className="text-left px-4 py-2">Action</th>
                  <th className="text-left px-4 py-2">Apply In</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                    No variances match the selected filters.
                  </td></tr>
                )}
                {filtered.map(v => (
                  <VarianceRow
                    key={v.id}
                    v={v}
                    locked={isFinalized && v.status === 'closed'}
                    onAction={() => setActionTarget(v)}
                    onChanged={loadAll}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Row errors */}
        {Array.isArray(imp.row_errors) && imp.row_errors.length > 0 && (
          <Card className="p-5 bg-rose-50 border-rose-200">
            <h3 className="text-sm font-semibold text-rose-900 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              Upload row errors ({imp.row_errors.length})
            </h3>
            <ul className="text-xs text-rose-800 space-y-1 max-h-48 overflow-y-auto list-disc list-inside">
              {imp.row_errors.slice(0, 50).map((e, i) => (
                <li key={i}>Row {e.row}: {e.error}</li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {actionTarget && (
        <VarianceActionModal
          variance={actionTarget}
          onClose={() => setActionTarget(null)}
          onSaved={() => { setActionTarget(null); loadAll(); }}
        />
      )}
    </MainLayout>
  );
}

function SumTile({ label, value, tone }) {
  const map = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] uppercase tracking-wide opacity-80 mt-1">{label}</div>
    </div>
  );
}

function VarianceRow({ v, locked, onAction, onChanged }) {
  const [approving, setApproving] = useState(false);
  const diff = Number(v.difference || 0);
  const diffCls = diff > 0 ? 'text-rose-700' : 'text-emerald-700';

  const approve = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/legacy-payroll/variances/${v.id}/approve`,
        { approved: true, remarks: '' },
        { withCredentials: true },
      );
      toast.success('Approved');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setApproving(false);
  };

  return (
    <tr className="border-b border-slate-100" data-testid={`variance-row-${v.id}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-800 text-sm">{v.employee_name || '—'}</div>
        <div className="text-xs text-slate-500">{v.employee_code}</div>
      </td>
      <td className="px-4 py-3 text-slate-700">{HEAD_LABEL[v.head] || v.head}</td>
      <td className="px-4 py-3 text-right tabular-nums">₹{Number(v.actual_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
      <td className="px-4 py-3 text-right tabular-nums">₹{Number(v.system_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
      <td className={`px-4 py-3 text-right tabular-nums font-semibold ${diffCls}`}>
        {diff > 0 ? '+' : ''}₹{Math.abs(diff).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </td>
      <td className="px-4 py-3 text-xs text-slate-700">
        {v.action_type ? ACTION_LABEL[v.action_type] || v.action_type : <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600">
        {v.adjustment_month_year && v.adjustment_month
          ? `${MONTHS[v.adjustment_month - 1]} ${v.adjustment_month_year}`
          : <span className="text-slate-400">—</span>}
        {v.applied_in_run_id && <span className="block text-[10px] text-emerald-700 mt-0.5">applied</span>}
      </td>
      <td className="px-4 py-3">
        <span className={`text-[11px] font-medium px-2 py-1 rounded border capitalize ${STATUS_TONE[v.status] || ''}`}>
          {v.status}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {!locked && v.status !== 'closed' && (
            <Button size="sm" variant="outline" onClick={onAction} data-testid={`variance-action-btn-${v.id}`}>
              {v.action_type ? 'Edit Action' : 'Set Action'}
            </Button>
          )}
          {v.status === 'actioned' && !locked && (
            <Button
              size="sm"
              onClick={approve}
              disabled={approving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`variance-approve-btn-${v.id}`}
            >
              {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Approve
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function VarianceActionModal({ variance, onClose, onSaved }) {
  const [actionType, setActionType] = useState(variance.action_type || 'adjust_in_next_payroll');
  const [year, setYear] = useState(variance.adjustment_month_year || new Date().getFullYear());
  const [month, setMonth] = useState(variance.adjustment_month || new Date().getMonth() + 1);
  const [reason, setReason] = useState(variance.reason || '');
  const [remarks, setRemarks] = useState(variance.remarks || '');
  const [saving, setSaving] = useState(false);

  const requiresMonth = ['adjust_in_next_payroll', 'recover_from_employee', 'refund_to_employee'].includes(actionType);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        action_type: actionType,
        adjustment_month_year: requiresMonth ? Number(year) : null,
        adjustment_month: requiresMonth ? Number(month) : null,
        reason,
        remarks,
      };
      await axios.post(
        `${API}/legacy-payroll/variances/${variance.id}/action`,
        body,
        { withCredentials: true },
      );
      toast.success('Action saved — now click Approve');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="variance-action-modal">
      <Card className="max-w-lg w-full p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Set Action</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
            <div><span className="text-slate-600">Employee:</span> <strong>{variance.employee_name}</strong> ({variance.employee_code})</div>
            <div><span className="text-slate-600">Head:</span> {HEAD_LABEL[variance.head] || variance.head}</div>
            <div><span className="text-slate-600">Difference:</span>{' '}
              <span className={Number(variance.difference) > 0 ? 'text-rose-700 font-semibold' : 'text-emerald-700 font-semibold'}>
                {Number(variance.difference) > 0 ? '+' : ''}₹{Math.abs(Number(variance.difference)).toLocaleString('en-IN')}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Action Type</label>
            <select
              data-testid="variance-action-type-select"
              value={actionType}
              onChange={e => setActionType(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm">
              {Object.entries(ACTION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          {requiresMonth && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium mb-1">Apply in Month</label>
                <select
                  data-testid="variance-adjustment-month"
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm">
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Apply in Year</label>
                <input
                  data-testid="variance-adjustment-year"
                  type="number" min={2020} max={2099}
                  value={year}
                  onChange={e => setYear(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium mb-1">Reason</label>
            <input
              data-testid="variance-reason"
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. PF over-deducted ₹600 in legacy register"
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Remarks</label>
            <textarea
              data-testid="variance-remarks"
              rows={2}
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="variance-action-save"
            onClick={save}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Action'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
